import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCustomToast } from 'hooks/useToast';
import { CommissionParams, getCommissions } from 'services/http/commission';
import { IPaginatedCommissions } from 'types/commission';
import { parseErrors, ResponseErrors } from 'utils/parseErrors';

export type CommissionType = 'Sale' | 'Margin' | 'Financing' | 'Dispatcher' | 'TCCB';

interface UseCommissionParams {
	company?: CommissionParams['company'];
	department?: CommissionParams['department'];
}

type CommissionScaleKey = 'scale1' | 'scale2' | 'scale3' | 'scale4' | 'scale5';

const SCALE_KEYS: CommissionScaleKey[] = ['scale1', 'scale2', 'scale3', 'scale4', 'scale5'];

type CommissionScales = Partial<Record<CommissionScaleKey, number>>;
type CommissionValue = Partial<Record<CommissionScaleKey, number>> & { base?: number | string };

// Soma a base com o percentual de cada escala já atingida (regra da comissão sobre venda).
const sumReachedScales = (totalSale: number, scales: CommissionScales, commission: CommissionValue) =>
	SCALE_KEYS.reduce((value, key) => {
		const threshold = scales[key];
		return threshold && totalSale >= threshold ? value + (Number(commission[key]) || 0) : value;
	}, Number(commission.base) || 0);

// Margem, Financiamento, Despachante e T.C.C.B.: base + percentual só da escala mais alta atingida (sem somar as intermediárias).
const baseWithHighestScale = (totalSale: number, scales: CommissionScales, commission: CommissionValue) => {
	const base = Number(commission.base) || 0;
	let value = base;

	SCALE_KEYS.forEach(key => {
		const threshold = scales[key];
		if (threshold && totalSale >= threshold) value = base + (Number(commission[key]) || 0);
	});

	return value;
};

export const useCommission = ({ company, department }: UseCommissionParams) => {
	const { addToast } = useCustomToast();

	const { data: commissions, isFetching: isCommissionsLoading } = useQuery<
		IPaginatedCommissions,
		AxiosError<ResponseErrors>,
		IPaginatedCommissions
	>(['commission', company, department], () => getCommissions({ company, department }), {
		onError: errors => addToast({ type: 'error', description: parseErrors(errors?.response?.data) }),
		enabled: !!company && !!department,
	});

	const commissionValues = useMemo(() => {
		const commission = commissions?.results.find(Boolean);

		const commissionScales = {
			scale1: commission?.scale1,
			scale2: commission?.scale2,
			scale3: commission?.scale3,
			scale4: commission?.scale4,
			scale5: commission?.scale5,
		};

		const findCommission = (possibleNames: string[]) =>
			commission?.indicatorComissionValues.find(item => possibleNames.includes(item.indicator.name));

		const commissionOnSale = findCommission(['Vendas Total', 'Faturamento']);
		const commissionOnMargin = findCommission(['Comissão sobre Margem', 'Margem de Contribuição']);
		const commissionOnFinancing = findCommission(['Comissão Financiamento', 'Financiamento']);
		const commissionOnDispatcher = findCommission(['Comissão Despachante', 'Emplacamento']);
		const commissionOnTCCB = findCommission(['Comissão tccb', 'TCCB']);

		return {
			commissionScales,
			commissionOnSale,
			commissionOnMargin,
			commissionOnFinancing,
			commissionOnDispatcher,
			commissionOnTCCB,
		};
	}, [commissions?.results]);

	const handleCommission = useCallback(
		(totalSale: number, type: CommissionType) => {
			const commission = commissionValues?.[`commissionOn${type}`];

			if (!commission) return 0;

			const scales = commissionValues.commissionScales;

			return type === 'Sale'
				? sumReachedScales(totalSale, scales, commission)
				: baseWithHighestScale(totalSale, scales, commission);
		},
		[commissionValues],
	);

	return { handleCommission, isCommissionsLoading };
};
